import { displayColumns, formatCell, sortValue } from './dbOverviewSections';

/**
 * Turns catalog records into what the shared results grid draws: a `result`
 * ({ columns, rows }) of display strings, plus the records in the same order
 * so a row index leads back to the object it shows.
 *
 * Pure — sorting and filtering are client-side (the whole section is one
 * catalog query, already in memory), and tested without React.
 */

/** Substring match on the name, and on the table/schema it belongs to. */
export function filterRecords(records, query) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return records;
  return records.filter((r) =>
    ['name', 'table', 'schema'].some((k) => r[k] != null && String(r[k]).toLowerCase().includes(q))
  );
}

/**
 * Stable multi-level sort. `levels` are the grid's `{ col, dir }` with `col`
 * the column KEY. Empty values sort last whichever the direction, the way
 * DataGrip treats NULLs in its object lists.
 */
export function sortRecords(records, levels) {
  if (!levels?.length) return records;
  const decorated = records.map((r, i) => ({ r, i }));
  decorated.sort((a, b) => {
    for (const { col, dir } of levels) {
      const va = sortValue(col, a.r[col]);
      const vb = sortValue(col, b.r[col]);
      const ea = va === null || va === '';
      const eb = vb === null || vb === '';
      if (ea !== eb) return ea ? 1 : -1;
      if (ea && eb) continue;
      let c;
      if (typeof va === 'number' && typeof vb === 'number') c = va - vb;
      else c = String(va).localeCompare(String(vb), undefined, { numeric: true });
      if (c !== 0) return dir === 'desc' ? -c : c;
    }
    return a.i - b.i;
  });
  return decorated.map((d) => d.r);
}

/**
 * @param labelFor  key → header text (translated)
 * @param sortLevels grid sort levels by KEY
 */
export function buildOverviewGrid({ engine, sectionId, records, filter, sortLevels, labelFor, locale, showSchema, yes }) {
  const keys = displayColumns(engine, sectionId, { showSchema });
  const visible = sortRecords(filterRecords(records || [], filter), sortLevels);
  const labels = keys.map((k) => labelFor(k));
  return {
    keys,
    records: visible,
    result: {
      columns: labels,
      rows: visible.map((r) => keys.map((k) => formatCell(k, r[k], { locale, yes }))),
    },
  };
}

/** Tab-separated text of the given records (header row first), for Ctrl+C. */
export function recordsToTsv(keys, labels, rows) {
  const clean = (s) => String(s ?? '').replace(/[\t\r\n]+/g, ' ');
  return [labels.map(clean).join('\t'), ...rows.map((row) => row.map(clean).join('\t'))].join('\n');
}
